import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@portal/ui";
import { logout } from "../api/client";
import { useMe } from "../api/queries";
import { AnnouncementsBell } from "./AnnouncementsBell";
import { ZenkLogo } from "./ZenkLogo";
import { ChevronDown, LogOut, Settings, ShieldCheck, UserRound } from "lucide-react";
import { UserSettingsDialog } from "../features/users/UserSettingsDialog";
import { ROLE_LABELS } from "../features/users/roles";
import { MemoFromUrl } from "../features/memos/MemoFromUrl";
import { Suspense, lazy, useEffect, useRef, useState } from "react";

const NAV: {
  to: "/" | "/projects" | "/customers" | "/admin";
  label: string;
  /** 管理者にだけ表示されるタブ(印を付けて、ほかのタブと見分けられるようにする) */
  adminOnly?: boolean;
}[] = [
  { to: "/", label: "ホーム" },
  { to: "/projects", label: "プロジェクト" },
  { to: "/customers", label: "顧客" },
];

// ゼンくん(2026-10-09 に本番にも出す。ユーザー指示)。AI のライブラリは大きいので、ボタンも含めて
// 遅れて読み込む(画面の表示を待たせない)。単体テストでは出さない
const ZenkunButton =
  import.meta.env.MODE !== "test"
    ? lazy(() =>
        import("../features/assistant/ZenkunButton").then((m) => ({ default: m.ZenkunButton })),
      )
    : null;

/** ログイン後の共通枠。ヘッダーで表示コンテキストを切り替える(SPEC §14.1)。 */
export function AppShell() {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // ユーザー設定のモーダル。fromNotice: 未登録の案内から開いた(最初から名前を編集する)
  const [naming, setNaming] = useState(false);
  const [fromNotice, setFromNotice] = useState(false);
  const openSettings = (editName: boolean) => {
    setFromNotice(editName);
    setNaming(true);
  };
  const doLogout = useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: "/login" });
    },
  });

  // 本文の領域だけをスクロールさせる(下の main)。画面を移ったら、先頭へ戻す
  const mainRef = useRef<HTMLElement>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [pathname]);

  const nav: typeof NAV = me.data?.user.roles.includes("SYSTEM_ADMIN")
    ? [...NAV, { to: "/admin", label: "管理", adminOnly: true }]
    : NAV;

  return (
    // 画面の高さいっぱいに、ヘッダー(固定の高さ)と本文(残り。ここだけがスクロールする)を縦に並べる。
    // ページ全体(window)はスクロールさせない。ヘッダーは動かず、スクロールバーもヘッダーに被らない
    // (ユーザー要望 2026-10-07)。sticky にすると、ダイアログを開いたときに body が overflow: hidden に
    // なって固定が外れ、スクロール済みならヘッダーが画面から消えていた。
    <div className="flex h-dvh flex-col bg-background">
      {/* ヘッダー: ロゴ(マーク + ZENK)と「社内ポータル」。上端にブランド色の細い帯を置く */}
      <header
        className={cn(
          "flex shrink-0 items-center justify-between border-b border-t-4 border-t-brand bg-background px-6 py-3",
          // ホーム以外は、下の区切り線にうっすら影を落とす(ホームは、ブックマークバーの下の線に付ける。2026-10-07)
          pathname !== "/" && "shadow-[0_2px_3px_-1px_rgb(0_0_0/0.14)]",
        )}
      >
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center text-foreground">
            <ZenkLogo subtitle="社内ポータル" />
          </Link>
          {/* ナビはロゴから離し、区切り線を入れて「名称の一部」に見えないようにする */}
          {/* 「候補」タブは置かない。候補はプロジェクト一覧の「新規プロジェクト ▼」→「候補から作成」から開く(2026-10-15) */}
          <nav aria-label="メイン" className="ml-2 flex items-center gap-1 border-l pl-6 text-sm">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className={cn(
                  "relative rounded-md px-3 py-1.5 text-muted-foreground hover:text-foreground [&.active]:font-medium [&.active]:text-foreground [&.active]:after:absolute [&.active]:after:inset-x-3 [&.active]:after:-bottom-[13px] [&.active]:after:h-0.5 [&.active]:after:bg-brand",
                )}
                activeOptions={{ exact: n.to === "/" }}
                // 管理者にだけ見えるタブは、盾の絵で見分けられる(「管理者のみ」のバッジと区切り線は付けない)ようにする(2026-10-07)
                {...(n.adminOnly ? { title: "管理者だけに表示されています" } : {})}
              >
                {n.adminOnly ? (
                  <span className="flex items-center gap-1.5" data-testid="admin-tab">
                    <ShieldCheck aria-hidden="true" className="size-4 text-brand" />
                    {n.label}
                  </span>
                ) : (
                  n.label
                )}
              </Link>
            ))}
          </nav>
        </div>
        {me.data && (
          <div className="flex items-center gap-2">
            {/* お知らせは、ユーザーの左隣のベル(ユーザー要望 2026-10-14) */}
            <AnnouncementsBell />
            {/* アカウントメニュー: ログアウトは日常操作ではないのでメニューの中に置く(ユーザー要望 2026-09-14) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="アカウントメニュー"
                  className="gap-1.5"
                >
                  {me.data.user.email}
                  <ChevronDown aria-hidden="true" className="size-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-48">
                {/* 自分の情報(旧ホームの「自分の情報」カード)。表示名は出さない(ユーザー要望 2026-10-01) */}
                <DropdownMenuLabel className="font-normal" data-testid="account-info">
                  <span className="block text-xs text-muted-foreground">
                    {me.data.user.roles.map((r) => ROLE_LABELS[r] ?? r).join("、") || "ロールなし"}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {/* 本人の設定(名前など)。本人も自分の名前を変えられる(ユーザー決定 2026-10-15) */}
                <DropdownMenuItem onSelect={() => openSettings(false)}>
                  <Settings />
                  ユーザー設定
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => doLogout.mutate()} disabled={doLogout.isPending}>
                  <LogOut />
                  ログアウト
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </header>
      {me.data && !me.data.user.nameRegistered && <NameNotice onSet={() => openSettings(true)} />}
      {me.data && (
        <UserSettingsDialog
          // 開くたびに、今の名前から始める(保存で版が変わっても作り直さない。「保存しました」を残すため)
          key={naming ? "open" : "closed"}
          user={me.data.user}
          startEditing={fromNotice}
          open={naming}
          onOpenChange={setNaming}
        />
      )}
      <main
        ref={mainRef}
        // ホームは、広い画面では画面全体をスクロールさせない(中のカラムがそれぞれスクロールする。2026-10-07)
        className={cn(
          "min-h-0 flex-1 p-6",
          pathname === "/" ? "overflow-y-auto lg:overflow-hidden" : "overflow-y-scroll",
        )}
      >
        <Outlet />
      </main>
      {/* 本文のメモへのリンク(?memo=)で、どの画面でもメモを開く(SPEC §13.6) */}
      <MemoFromUrl />
      {/* ゼンくん(2026-10-09 から本番にも出す) */}
      {ZenkunButton && (
        <Suspense fallback={null}>
          <ZenkunButton />
        </Suspense>
      )}
    </div>
  );
}

/**
 * 名前が未登録の人への案内(ユーザー決定 2026-10-15)。本人が自分で設定できる(使うのは妨げない)。
 */
function NameNotice({ onSet }: { onSet: () => void }) {
  return (
    <div
      role="status"
      data-testid="name-notice"
      className="flex shrink-0 items-center gap-2 border-b bg-amber-50 px-6 py-2 text-sm text-amber-900"
    >
      <UserRound aria-hidden="true" className="size-4 shrink-0" />
      名前が未登録です。
      <Button variant="outline" size="sm" className="h-7 bg-white" onClick={onSet}>
        名前を設定する
      </Button>
    </div>
  );
}
